import { useCallback, useMemo, useState } from 'react'
import { useAppSelector } from 'reduxTyped'
import { l } from '@manychat/manyui'

import { localStorage } from 'utils'
import { AiImproverLocalStorage } from 'apps/aiTextImprover/lib/localStorage'
import { OnImprovementSubmit } from 'apps/aiTextImprover/lib/signatures'
import { RateFeatureType, RateType, Step, AiImproverGoal } from 'apps/aiTextImprover/lib/types'
import { AiAnalytics } from 'common/ai/lib/analytics'
import { billing, UpgradeSource } from 'common/billing'
import { AddOnSlug } from 'common/billing/interfaces/addOnTypes'
import { getHasBillingAccess } from 'common/billing/selectors/billingSelectors'
import { alert } from 'common/core'
import { Ability } from 'common/core/constants/Ability'
import { useAbility } from 'common/core/hooks/useAbility'
import { getCurrentAccountID } from 'common/core/selectors/appSelectors'
import { ImproveTextRequestData } from 'shared/api/requests/aiContentGenerator/schemas'

interface UseAiTextImproverProps {
  initialText: string
  onSubmit: OnImprovementSubmit
  flowNs?: string
}

const PROMOTE_ADDON_STEP_FREQUENCY = 10
const RATING_STEP_FREQUENCY = 2

export const useAiTextImprover = ({ initialText, onSubmit, flowNs }: UseAiTextImproverProps) => {
  const accountId = useAppSelector(getCurrentAccountID)
  const hasBillingAccess = useAppSelector(getHasBillingAccess)
  const hasAgentsAbility = useAbility(Ability.AI_AGENT)

  const [activeStep, setActiveStep] = useState(
    initialText === '' ? Step.WRITE_TEXT : Step.CHOOSE_GOAL,
  )
  const [text, setText] = useState(initialText)
  const [goal, setGoal] = useState<AiImproverGoal | null>(null)
  const [subGoal, setSubGoal] = useState<string | null>(null)
  const [improvedText, setImprovedText] = useState('')

  const generationData = useMemo((): ImproveTextRequestData | null => {
    if (!goal) {
      return null
    }

    return {
      source: text,
      goal,
      goal_value: subGoal || null,
      flow_ns: flowNs || null,
    }
  }, [flowNs, goal, subGoal, text])

  const openPreviousStep = useCallback(() => {
    setGoal(null)
    setSubGoal(null)
    setActiveStep(Step.CHOOSE_GOAL)
  }, [])

  const submitNewText = useCallback(
    (text: string) => {
      setText(text)

      onSubmit({
        text: text,
        rating: null,
        rateStepWasShown: false,
        upgradeStepWasShown: false,
        upgradeClicked: false,
        isFinished: false,
      })

      setActiveStep(Step.CHOOSE_GOAL)
    },
    [onSubmit],
  )

  const selectGoal = useCallback(
    ({ value, additionalValue }: { value: AiImproverGoal; additionalValue?: string }) => {
      setGoal(value)
      setSubGoal(additionalValue ?? null)
      setActiveStep(Step.CHOOSE_IMPROVEMENT)
    },
    [],
  )

  const submitImprovedText = useCallback(
    (improvedText: string) => {
      setImprovedText(improvedText)

      const improvementsCountKey = AiImproverLocalStorage.getImprovementsCount(accountId)
      const improvementsCount = (Number(localStorage.getItem(improvementsCountKey)) || 0) + 1
      localStorage.setItem(improvementsCountKey, improvementsCount)

      const shouldOpenPromoteStep =
        (improvementsCount === 2 || (improvementsCount - 2) % PROMOTE_ADDON_STEP_FREQUENCY === 0) &&
        hasBillingAccess &&
        !hasAgentsAbility
      const shouldOpenRateStep =
        improvementsCount === 1 || (improvementsCount - 1) % RATING_STEP_FREQUENCY === 0

      if (shouldOpenPromoteStep || shouldOpenRateStep) {
        onSubmit({
          text: improvedText,
          rating: null,
          rateStepWasShown: false,
          upgradeStepWasShown: false,
          upgradeClicked: false,
          isFinished: false,
        })
      }

      if (shouldOpenPromoteStep) {
        return setActiveStep(Step.PROMOTE_ADDON)
      }

      if (shouldOpenRateStep) {
        return setActiveStep(Step.RATE_RESULT)
      }

      onSubmit({
        text: improvedText,
        rating: null,
        rateStepWasShown: false,
        upgradeStepWasShown: false,
        upgradeClicked: false,
        isFinished: true,
      })
    },
    [accountId, hasAgentsAbility, hasBillingAccess, onSubmit],
  )

  const submitRating = useCallback(
    async (rating: RateType | null) => {
      onSubmit({
        text: improvedText,
        rating,
        rateStepWasShown: true,
        upgradeStepWasShown: false,
        upgradeClicked: false,
        isFinished: true,
      })

      if (!rating || !generationData) {
        return
      }

      alert(l.translate('Thank you for your feedback'), 'success')

      AiAnalytics.logReaction({
        feature: RateFeatureType.TEXT_IMPROVER,
        reaction: rating,
        text: improvedText,
        context: generationData,
      })
    },
    [generationData, improvedText, onSubmit],
  )

  const submitUpgrade = useCallback(
    (upgradeClicked: boolean) => {
      onSubmit({
        text: improvedText,
        rating: null,
        rateStepWasShown: false,
        upgradeStepWasShown: true,
        upgradeClicked,
        isFinished: true,
      })

      if (upgradeClicked) {
        billing.requestUpgrade({
          source: UpgradeSource.AI_TEXT_IMPROVER,
          addOns: [AddOnSlug.AI],
        })
      }
    },
    [improvedText, onSubmit],
  )

  return {
    activeStep,
    text,
    goal,
    subGoal,
    generationData,

    submitNewText,
    selectGoal,
    openPreviousStep,
    submitImprovedText,
    submitRating,
    submitUpgrade,
  }
}
